import React from "react";
import { RC_ICON } from "../lib/constants";
import { Printer } from "lucide-react";

const Section = ({ title, children }) => (
  <section className="mt-7">
    <h2 className="font-display text-xl font-semibold text-vin border-b border-[var(--line)] pb-2 mb-3">{title}</h2>
    {children}
  </section>
);

export default function Facilitator() {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="flex items-start justify-between gap-4 print:hidden">
          <p className="text-sm text-ink-soft">A one-page guide for leading the experience at your assembly or conference.</p>
          <button
            data-testid="print-facilitator-button"
            onClick={() => window.print()}
            className="shrink-0 inline-flex items-center gap-2 bg-vin text-white text-sm font-semibold px-4 py-2 rounded-full hover:bg-vin-hover transition-colors duration-200"
          >
            <Printer className="w-4 h-4" strokeWidth={1.75} /> Print
          </button>
        </div>

        <header className="mt-6 flex items-center gap-4">
          <img src={RC_ICON} alt="Root Causes Challenge" className="h-14 w-14 rounded-lg" />
          <div>
            <h1 className="font-display text-2xl font-semibold text-ink leading-tight">Root Causes Challenge</h1>
            <p className="text-sm text-ink-soft">Facilitator Guide · An Interactive Poverty Simulation</p>
          </div>
        </header>

        <Section title="Purpose">
          <p className="text-sm text-ink leading-relaxed">
            This is a formation experience — not a game and not about saving money. It helps participants
            understand how poverty limits choices and forces impossible trade-offs, so we can listen more
            deeply, respond to the whole person, and advocate for full participation — guided by the principles
            of human dignity and the common good.
          </p>
        </Section>

        <Section title="How to run it (about 15–20 minutes)">
          <ol className="text-sm text-ink leading-relaxed space-y-1.5 list-decimal pl-5">
            <li>Have participants scan the QR code (or open the link) on their phones — the experience is anonymous, about 10–15 minutes.</li>
            <li>Read the "Walk in their shoes" instruction aloud before they begin.</li>
            <li>Let each person complete a year as their assigned neighbor, pausing at the reflection prompts.</li>
            <li>Gather at tables and work through the discussion questions below.</li>
            <li>Invite members to add their own community using "Contribute your community."</li>
          </ol>
        </Section>

        <Section title="Read aloud before beginning">
          <p className="text-sm text-ink italic leading-relaxed">
            "Answer as though you are living the daily reality of a neighbor — not from your own income,
            education, savings, credit, transportation, or support network. The goal is not to win or
            save money, but to experience how limited resources shape everyday decisions. There are no
            wrong answers."
          </p>
        </Section>

        <Section title="Reflection prompts (during the experience)">
          <ul className="text-sm text-ink leading-relaxed space-y-1.5 list-disc pl-5">
            <li>What barriers are shaping this decision?</li>
            <li>What choices are actually available to me?</li>
            <li>What am I sacrificing by making this choice?</li>
            <li>What happens if I delay this decision?</li>
            <li>How will this affect the rest of my week or month?</li>
          </ul>
        </Section>

        <Section title="Discussion questions (after the experience)">
          <ol className="text-sm text-ink leading-relaxed space-y-1.5 list-decimal pl-5">
            <li>Which barrier surprised you most — and why?</li>
            <li>Where did you see one problem cascade into another?</li>
            <li>What did your neighbor have to go without?</li>
            <li>How might these same barriers show up in our own community?</li>
            <li>What would it take to address a root cause, not only the emergency?</li>
            <li>How can we advocate for full participation, not just provide relief?</li>
          </ol>
        </Section>

        <Section title="Who this represents">
          <p className="text-sm text-ink leading-relaxed">
            Many carry these realities every day — among them returning citizens, families in generational
            poverty, grandparents raising grandchildren, and young people aging out of foster care.
          </p>
        </Section>

        <p className="mt-10 text-xs text-ink-soft">
          "Before we offer advice, we must first understand the lived experience of our neighbor."
        </p>
      </div>
    </div>
  );
}
